import React, { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import MobileViewContainer from "../../Components/MobileViewContainer";
import Navbar from "../../Components/Common/navbar";
import NavBottomBar from "../../Components/Common/NavBottomBar";
import {
  APP_NOTIFICATION_QUERY_KEY,
  NOTIFICATION_COUNT_QUERY_KEY,
  notificationService,
} from "../../api/services/notification.service";

const parseNextPage = (nextUrl) => {
  if (!nextUrl) return undefined;

  try {
    const parsedUrl = new URL(nextUrl);
    const page = Number(parsedUrl.searchParams.get("page"));
    if (Number.isFinite(page) && page > 0) {
      return page;
    }
  } catch (error) {
    const match = nextUrl.match(/[?&]page=(\d+)/);
    if (match) {
      return Number(match[1]);
    }
  }

  return undefined;
};

const formatPushAt = (dateString) => {
  if (!dateString) return "-";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function NotificationPage() {
  const queryClient = useQueryClient();
  const [readingIds, setReadingIds] = useState([]);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: APP_NOTIFICATION_QUERY_KEY,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await notificationService.getNotifications(pageParam);
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => parseNextPage(lastPage?.next),
  });

  const notifications = useMemo(
    () => data?.pages?.flatMap((page) => page?.results || []) || [],
    [data]
  );

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      await notificationService.readNotificationById(notificationId);
      return notificationId;
    },
    onSuccess: (_, notificationId) => {
      queryClient.setQueryData(APP_NOTIFICATION_QUERY_KEY, (previousData) => {
        if (!previousData) return previousData;

        return {
          ...previousData,
          pages: previousData.pages.map((page) => ({
            ...page,
            results: page.results.map((item) => {
              if (item.id !== notificationId) return item;
              if (item.read_at) return item;

              return {
                ...item,
                read_at: new Date().toISOString(),
              };
            }),
          })),
        };
      });

      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_QUERY_KEY });
    },
    onSettled: (_, __, notificationId) => {
      setReadingIds((prev) => prev.filter((id) => id !== notificationId));
    },
  });

  const handleNotificationClick = (notification) => {
    if (notification.read_at) return;
    if (readingIds.includes(notification.id)) return;

    setReadingIds((prev) => [...prev, notification.id]);
    markAsReadMutation.mutate(notification.id);
  };

  return (
    <MobileViewContainer>
      <Navbar />

      <div className="px-4 py-5 pb-28">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">알림</h4>

        {isLoading ? (
          <div className="py-20 text-center text-gray-500">알림을 불러오는 중입니다...</div>
        ) : null}

        {isError ? (
          <div className="py-20 text-center text-gray-500">
            <p className="mb-3">알림을 불러오지 못했습니다.</p>
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm"
              onClick={() => refetch()}
            >
              다시 시도
            </button>
          </div>
        ) : null}

        {!isLoading && !isError && notifications.length === 0 ? (
          <div className="py-20 text-center text-gray-500">도착한 알림이 없습니다.</div>
        ) : null}

        {!isLoading && !isError && notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.map((notification) => {
              const isUnread = !notification.read_at;
              const isReading = readingIds.includes(notification.id);

              return (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left rounded-xl border px-4 py-4 transition-colors ${
                    isUnread
                      ? "border-red-200 bg-red-50 hover:bg-red-100"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="font-semibold text-gray-900 break-all">{notification.title || "알림"}</p>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isUnread
                          ? "bg-red-500 text-white"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {isReading ? "처리중" : isUnread ? "읽지 않음" : "읽음"}
                    </span>
                  </div>

                  <p className="text-sm text-gray-700 whitespace-pre-line break-all">{notification.body}</p>

                  <div className="mt-2 text-xs text-gray-500">
                    {formatPushAt(notification.created_at)}
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}

        {!isLoading && !isError && hasNextPage ? (
          <div className="mt-5 text-center">
            <button
              type="button"
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "불러오는 중..." : "더보기"}
            </button>
          </div>
        ) : null}
      </div>

      <NavBottomBar activeBar={5} />
    </MobileViewContainer>
  );
}
